import React from "react";

import {
  Callout,
  Button,
  Card,
  LinkButton,
  MoneyInput,
  Pill,
  Skeleton,
  useToast,
  parseMoney,
} from "../design";
import { getMyAlerts, setMyAlerts, type MyAlerts } from "../lib/api";
import type { AuthHeaders } from "../lib/auth";

type Key = keyof MyAlerts;

const money = (v: string): string =>
  `$${Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

const ROWS: {
  key: Key;
  title: string;
  sub: string;
  suffix?: string;
  on: (usd: string) => string;
}[] = [
  {
    key: "session_usd",
    title: "A single session costs more than",
    sub: "Catches an agent stuck in a loop. At API prices, once per session.",
    on: (usd) => `On: emails you when a session passes ${usd}`,
  },
  {
    key: "month_usd",
    title: "This month's billed spend passes",
    sub: "Pay-per-token usage only; a flat plan never counts. Once a month.",
    suffix: "/mo",
    on: (usd) => `On: emails you when this month passes ${usd}`,
  },
];

/** Your own email alerts: a runaway session, and the month's billed spend. */
export function SpendAlerts({ authHeaders }: { authHeaders: AuthHeaders }) {
  const [saved, setSaved] = React.useState<MyAlerts | null>(null);
  const [draft, setDraft] = React.useState<Record<Key, string>>({
    session_usd: "",
    month_usd: "",
  });
  const [busy, setBusy] = React.useState<Key | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const toast = useToast();

  React.useEffect(() => {
    getMyAlerts(authHeaders).then(
      (a) => {
        setSaved(a);
        setDraft({
          session_usd: a.session_usd ?? "",
          month_usd: a.month_usd ?? "",
        });
      },
      () => setError("Couldn't load your alerts."),
    );
  }, [authHeaders]);

  async function save(key: Key) {
    if (!saved) return;
    const value = draft[key].trim().replace(/,/g, "");
    if (value !== "" && !((parseMoney(value) ?? 0) > 0)) {
      setError(
        "Enter an amount above $0, or leave it empty to turn the alert off.",
      );
      return;
    }
    setBusy(key);
    setError(null);
    try {
      const next = await setMyAlerts(authHeaders, { [key]: value || null });
      setSaved(next);
      toast(value ? `Alert set at $${value}.` : "Alert turned off.");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card
      title="Spend alerts"
      info="Emailed to your sign-in address. Leave an amount empty to turn that alert off."
    >
      {!saved && !error ? (
        <Skeleton lines={2} />
      ) : (
        <div className="per-rows">
          {ROWS.map((r) => {
            const dirty =
              !!saved && draft[r.key].trim() !== (saved[r.key] ?? "");
            const live = saved?.[r.key];
            return (
              <div key={r.key} className="per-row">
                <span className="per-row-text">
                  <span className="per-row-title">{r.title}</span>
                  <span className="per-row-sub">{r.sub}</span>
                  {saved && (
                    <span className="per-alert-state">
                      {live ? (
                        <Pill tone="ok" icon="check">
                          {r.on(money(live))}
                        </Pill>
                      ) : (
                        <Pill>Off</Pill>
                      )}
                    </span>
                  )}
                </span>
                <span className="per-alert-ctl">
                  <MoneyInput
                    aria-label={r.title}
                    placeholder="Not set"
                    suffix={draft[r.key].trim() ? r.suffix : undefined}
                    value={draft[r.key]}
                    onChange={(e) =>
                      setDraft((d) => ({ ...d, [r.key]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && dirty) void save(r.key);
                    }}
                  />
                  <Button
                    disabled={!dirty}
                    busy={busy === r.key}
                    onClick={() => void save(r.key)}
                  >
                    Save
                  </Button>
                </span>
              </div>
            );
          })}
          {error && (
            <div className="ds-field-error" role="alert">
              {error}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** A one-line prompt on My usage until either alert is set. */
export function AlertsNudge({ authHeaders }: { authHeaders: AuthHeaders }) {
  const [unset, setUnset] = React.useState(false);
  React.useEffect(() => {
    getMyAlerts(authHeaders).then(
      (a) => setUnset(!a.session_usd && !a.month_usd),
      () => {},
    );
  }, [authHeaders]);
  if (!unset) return null;
  return (
    <Callout
      icon="mail"
      action={
        <LinkButton size="sm" href="#account?tab=notifications">
          Set up spend alerts
        </LinkButton>
      }
    >
      Get an email when a single session gets expensive or this month&apos;s
      spend passes a line you choose.
    </Callout>
  );
}
